/**
 * Dialog de reversão de bulk_assign — 3 estratégias:
 *
 *   1. "backup"  → usa snapshot do backup automático anterior à operação
 *   2. "logs"    → infere pelo histórico de audit_logs mobile (confirm_receipt,
 *                  status_change etc). Fallback quando o backup foi apagado
 *                  no restart do pod K8s.
 *   3. "unset"   → zera entregador_id + volta status pra "aguardando".
 *                  Último recurso quando nem backup nem logs mobile ajudam.
 *
 * UX: usuário escolhe a estratégia no topo, cada uma tem dry-run automático.
 * Apply exige digitar "REVERTER" pra confirmar.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AlertTriangle, Undo2, Loader2, ShieldCheck, CheckCircle2, Database, Radio, Eraser } from "lucide-react";

const CONFIRM_WORD = "REVERTER";

const STRATEGIES = {
  snapshot: {
    endpoint: "/admin/tools/snapshot-revert-bulk-assign",
    icon: ShieldCheck,
    title: "Snapshot exato",
    description: "Usa o entregador_anterior gravado no próprio audit_log — 100% preciso. Só funciona pra bulks feitos a partir de fev/2026.",
  },
  backup: {
    endpoint: "/admin/tools/revert-bulk-assign",
    icon: Database,
    title: "Backup automático",
    description: "Usa o snapshot do backup mais recente antes da operação. Fev/2026: lê do Object Storage se o zip sumiu do disco.",
  },
  logs: {
    endpoint: "/admin/tools/infer-previous-entregadores",
    icon: Radio,
    title: "Inferência por logs",
    description: "Descobre quem era o entregador anterior olhando ações mobile que ele fez (recebimento, bipagem, foto). Fallback pra quando o backup foi apagado.",
  },
  unset: {
    endpoint: "/admin/tools/bulk-unset-entregador",
    icon: Eraser,
    title: "Reset pra aguardando",
    description: "Zera o entregador das 97 e volta status pra 'aguardando'. Perde a informação, mas desfaz o erro. Depois você reatribui manualmente.",
  },
};

export default function RevertBulkAssignDialog({ auditLog, open, onOpenChange, onDone }) {
  const [strategy, setStrategy] = useState("snapshot");
  const [phase, setPhase] = useState("idle"); // idle | loading | preview | applying | done | error
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);

  const cfg = STRATEGIES[strategy];

  useEffect(() => {
    if (!open) {
      setStrategy("snapshot"); setPhase("idle"); setPlan(null);
      setError(""); setConfirmText(""); setResult(null);
    }
  }, [open]);

  // Roda dry-run toda vez que muda a estratégia OU abre o dialog
  useEffect(() => {
    if (!open || !auditLog) return;
    let cancelled = false;
    setPhase("loading");
    setPlan(null);
    setError("");
    setResult(null);
    setConfirmText("");

    api.post(cfg.endpoint, {
      audit_log_id: auditLog.id,
      dry_run: true,
    }).then((r) => {
      if (cancelled) return;
      setPlan(r.data);
      setPhase("preview");
    }).catch((e) => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || e?.message || "Falha ao carregar o plano");
      setPhase("error");
    });
    return () => { cancelled = true; };
  }, [open, auditLog, strategy, cfg.endpoint]);

  const apply = async () => {
    setPhase("applying");
    try {
      const body = { audit_log_id: auditLog.id, dry_run: false, confirm: true };
      if (strategy === "logs") body.min_confidence = "medium";
      const r = await api.post(cfg.endpoint, body);
      setResult(r.data);
      setPhase("done");
      const n = r.data.applied_count ?? r.data.affected_count ?? 0;
      toast.success(`Reversão aplicada: ${n} entrega(s) alteradas`);
      onDone?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Falha ao aplicar");
      setPhase("error");
    }
  };

  const eligibleCount = useMemo(() => {
    if (!plan) return 0;
    if (strategy === "snapshot") return (plan.stats?.revert || 0) + (plan.stats?.revert_to_null || 0);
    if (strategy === "backup") return (plan.stats?.revert || 0) + (plan.stats?.revert_to_null || 0);
    if (strategy === "logs") return (plan.stats?.high || 0) + (plan.stats?.medium || 0);
    if (strategy === "unset") return plan.would_affect || 0;
    return 0;
  }, [plan, strategy]);

  const canApply = phase === "preview" && confirmText.trim().toUpperCase() === CONFIRM_WORD && eligibleCount > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="revert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-orange-600" />
            Reverter atribuição em massa
          </DialogTitle>
          <DialogDescription>
            Escolha a estratégia mais compatível com o que sobrou no sistema.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={strategy} onValueChange={setStrategy} className="w-full">
          <TabsList className="grid w-full grid-cols-4" data-testid="revert-strategy-tabs">
            <TabsTrigger value="snapshot" data-testid="tab-snapshot">
              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Exato
            </TabsTrigger>
            <TabsTrigger value="backup" data-testid="tab-backup">
              <Database className="w-3.5 h-3.5 mr-1" /> Backup
            </TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-logs">
              <Radio className="w-3.5 h-3.5 mr-1" /> Logs
            </TabsTrigger>
            <TabsTrigger value="unset" data-testid="tab-unset">
              <Eraser className="w-3.5 h-3.5 mr-1" /> Reset
            </TabsTrigger>
          </TabsList>
          {["snapshot","backup","logs","unset"].map((s) => (
            <TabsContent key={s} value={s} className="text-xs text-muted-foreground mt-2">
              {STRATEGIES[s].description}
            </TabsContent>
          ))}
        </Tabs>

        {phase === "loading" && (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground" data-testid="revert-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Calculando plano…
          </div>
        )}

        {phase === "error" && (
          <Alert variant="destructive" data-testid="revert-error">
            <AlertTriangle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {phase === "preview" && plan && strategy === "snapshot" && <SnapshotPreview plan={plan} />}
        {phase === "preview" && plan && strategy === "backup" && <BackupPreview plan={plan} />}
        {phase === "preview" && plan && strategy === "logs" && <LogsPreview plan={plan} />}
        {phase === "preview" && plan && strategy === "unset" && <UnsetPreview plan={plan} />}

        {phase === "preview" && eligibleCount > 0 && (
          <div>
            <Label htmlFor="revert-confirm" className="text-sm">
              Digite <b>{CONFIRM_WORD}</b> pra aplicar em <b>{eligibleCount}</b> entrega(s):
            </Label>
            <Input
              id="revert-confirm"
              data-testid="revert-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_WORD}
              className="mt-2 font-mono uppercase"
              autoFocus
            />
          </div>
        )}

        {phase === "applying" && (
          <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" /> Aplicando…
          </div>
        )}

        {phase === "done" && result && (
          <Alert data-testid="revert-done">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <AlertDescription>
              <b>{result.applied_count ?? result.affected_count ?? 0}</b> entrega(s) alteradas com sucesso.
              {result.failed?.length > 0 && (
                <div className="mt-1 text-amber-700 text-xs">
                  Falharam {result.failed.length} — verifique nos logs de auditoria.
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          {phase === "done" || phase === "error" ? (
            <Button onClick={() => onOpenChange(false)} data-testid="revert-close">Fechar</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={phase === "applying"}>
                Cancelar
              </Button>
              <Button
                onClick={apply}
                disabled={!canApply || phase !== "preview"}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="revert-apply-btn"
              >
                <ShieldCheck className="w-4 h-4 mr-1" />
                Aplicar agora
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SnapshotPreview({ plan }) {
  const s = plan.stats || {};
  return (
    <div className="space-y-3" data-testid="revert-preview-snapshot">
      <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-1">
        <div className="font-semibold text-emerald-800 flex items-center gap-1.5">
          <ShieldCheck className="w-4 h-4" /> Estratégia mais precisa
        </div>
        <div className="text-xs text-emerald-700">
          O estado anterior de cada entrega foi gravado no próprio audit_log. Reversão 100% fiel — sem depender de backup ou logs de mobile.
        </div>
        <div className="pt-1"><b>Log original:</b> {plan.audit_log?.created_at} · {plan.audit_log?.user_email}</div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <StatBox label="Total" value={s.total} tone="slate" />
        <StatBox label="Reverte" value={s.revert} tone="emerald" testid="stat-snap-revert" />
        <StatBox label="Volta null" value={s.revert_to_null} tone="blue" testid="stat-snap-null" />
        <StatBox label="Já mudou" value={s.skip_already_reassigned} tone="amber" testid="stat-snap-skip" />
      </div>
      {s.skip_already_reassigned > 0 && (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-xs">
            <b>{s.skip_already_reassigned}</b> entrega(s) já foram reatribuídas a outro entregador depois do bulk — não serão tocadas (respeita a ação posterior).
          </AlertDescription>
        </Alert>
      )}
      {s.skip_missing_now > 0 && (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-xs">
            <b>{s.skip_missing_now}</b> entrega(s) foram apagadas depois — não recuperáveis via revert.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function BackupPreview({ plan }) {
  const s = plan.stats || {};
  return (
    <div className="space-y-3" data-testid="revert-preview-backup">
      <div className="bg-muted rounded p-3 text-sm space-y-1">
        <div><b>Log original:</b> {plan.audit_log.created_at} · {plan.audit_log.user_email}</div>
        <div><b>Backup usado:</b> {plan.backup_used?.filename}
          <span className="text-muted-foreground"> ({plan.backup_used?.started_at})</span></div>
        {plan.alternative_backups_on_disk?.length > 1 && (
          <div className="text-xs text-muted-foreground pt-1">
            Alternativas: {plan.alternative_backups_on_disk.filter(b => b.filename !== plan.backup_used?.filename).map(b => b.filename).slice(0, 3).join(", ")}
            {plan.alternative_backups_on_disk.length > 4 ? "…" : ""}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
        <StatBox label="Total" value={s.total} tone="slate" />
        <StatBox label="Reverte" value={s.revert} tone="emerald" testid="stat-revert" />
        <StatBox label="Volta null" value={s.revert_to_null} tone="blue" testid="stat-revert-null" />
        <StatBox label="Sem backup" value={s.skip_not_in_backup} tone="amber" testid="stat-skip" />
        <StatBox label="Apagadas" value={s.skip_missing_now} tone="rose" testid="stat-skip-missing" />
      </div>
    </div>
  );
}

function LogsPreview({ plan }) {
  const s = plan.stats || {};
  return (
    <div className="space-y-3" data-testid="revert-preview-logs">
      <div className="bg-muted rounded p-3 text-sm">
        <div><b>Log original:</b> {plan.audit_log?.created_at} · {plan.audit_log?.user_email}</div>
        <div className="text-xs text-muted-foreground mt-1">
          Aplica só entregas com confidence <b>high</b> (aceite/foto/bipagem) ou <b>medium</b> (mudança de status). Skip nas demais.
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <StatBox label="Total" value={s.total} tone="slate" />
        <StatBox label="High" value={s.high} tone="emerald" testid="stat-high" />
        <StatBox label="Medium" value={s.medium} tone="blue" testid="stat-medium" />
        <StatBox label="Sem pista" value={s.no_log} tone="amber" testid="stat-nolog" />
      </div>
      {s.no_log > 0 && (
        <Alert>
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription className="text-xs">
            <b>{s.no_log}</b> entrega(s) não têm ação mobile anterior — precisarão de reatribuição manual (ou use a estratégia Reset).
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

function UnsetPreview({ plan }) {
  return (
    <div className="space-y-3" data-testid="revert-preview-unset">
      <Alert>
        <AlertTriangle className="w-4 h-4" />
        <AlertDescription className="text-sm">
          Vai zerar <b>{plan.would_affect}</b> de <b>{plan.total_in_log}</b> entrega(s) do log e voltar o status pra <b>{plan.target_status}</b>. As demais foram reatribuídas a outro entregador após a operação errada e serão preservadas.
        </AlertDescription>
      </Alert>
    </div>
  );
}

function StatBox({ label, value, tone = "slate", testid }) {
  const toneClasses = {
    slate: "bg-slate-100 text-slate-800",
    emerald: "bg-emerald-100 text-emerald-800",
    blue: "bg-blue-100 text-blue-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
  }[tone];
  return (
    <div className={`rounded p-2 ${toneClasses}`} data-testid={testid}>
      <div className="text-xl font-bold leading-tight">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );
}
